/**
 * Session-scoped PAS deliberate-practice hatch runner.
 * Survives drawer close / leaving the asset screen; dies with the JS process.
 *
 * Modes:
 * - sequential: math/yuwen — generate-one loop, append to library immediately
 * - english_batch: one FC /generate pack → previewDrafts; confirm then library
 */

import { assessPasHatchComplexity } from '../utils/pasHatchComplexity';
import {
  createPasPracticeLibraryItem,
  listPasPracticeNegativeExampleQuestions,
  refreshPasPracticeLibrary,
  type PasPracticeLibraryItem,
} from '../utils/pasPracticeLibrary';
import {
  generatePasPracticeDraftBatch,
  generatePasPracticeDraftOne,
  type PasPkpDraft,
} from './pasMathPkpService';

/** Vision/OCR-style complaints must never be shown as hatch generate errors. */
function isVisionStyleComplaint(text: string): boolean {
  const t = String(text || '').trim();
  if (!t) return false;
  return /图中仅|完全没有题干|条件不完备|没有指令|疑似为|手写体|框选区|识图|OCR|缺少题干|问题描述/.test(t)
    && (t.length > 36 || /歧义|不完备|没有题干/.test(t));
}

function asHatchFailMessage(raw: string): string {
  const t = String(raw || '').trim();
  if (!t) return '出题未返回草稿，请重试';
  if (isVisionStyleComplaint(t)) {
    return '出题未返回草稿。识图不完整不阻断出题，请再点「生成一道题」。';
  }
  return t;
}

export type PasHatchProgress = {
  current: number;
  total: number;
  produced?: number;
  failed?: number;
};

export type PasHatchMode = 'sequential' | 'english_batch';

export type PasHatchJobSnapshot = {
  key: string;
  assetId: string;
  regionId: string;
  running: boolean;
  stopping: boolean;
  usingProModel: boolean;
  mode: PasHatchMode;
  progress: PasHatchProgress | null;
  items: PasPracticeLibraryItem[];
  /** English batch: drafts awaiting confirm (not yet in library). */
  previewDrafts: PasPkpDraft[];
  error: string | null;
  offerProRetry: boolean;
  modelFeedback: string | null;
  hatchPrompt: string;
};

export type PasHatchStartInput = {
  assetId: string;
  regionId: string;
  sourceKpId?: string | null;
  hatchPrompt: string;
  /** Route AppFunctor → pas-{subject}-pkp FC. */
  subject?: string | null;
  regionContent?: string | null;
  regionFeedback?: string | null;
  cropRectNormalized?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Library to append onto (usually current region items). */
  initialItems: PasPracticeLibraryItem[];
  /** sequential only; english_batch infers count on FC. */
  count?: number;
  mode?: PasHatchMode;
  useProModel?: boolean;
  onToast?: (message: string) => void;
  /** Persist vision text back onto the region when auto-识图 succeeds. */
  onRegionVision?: (vision: { regionContent: string; regionFeedback: string }) => void;
};

export type PasHatchAssetPersist = {
  persistItems: (regionId: string, items: PasPracticeLibraryItem[]) => void | Promise<void>;
  onToast?: (message: string) => void;
};

type Listener = (snapshot: PasHatchJobSnapshot) => void;

type JobState = {
  key: string;
  assetId: string;
  regionId: string;
  abort: boolean;
  running: boolean;
  stopping: boolean;
  usingProModel: boolean;
  mode: PasHatchMode;
  progress: PasHatchProgress | null;
  items: PasPracticeLibraryItem[];
  previewDrafts: PasPkpDraft[];
  error: string | null;
  offerProRetry: boolean;
  modelFeedback: string | null;
  hatchPrompt: string;
  regionContent: string;
  regionFeedback: string;
  cropRectNormalized?: PasHatchStartInput['cropRectNormalized'];
  sourceKpId?: string | null;
  subject?: string | null;
  runToken: number;
};

function buildKey(assetId: string, regionId: string) {
  return `${String(assetId || '').trim()}::${String(regionId || '').trim()}`;
}

function toSnapshot(job: JobState): PasHatchJobSnapshot {
  return {
    key: job.key,
    assetId: job.assetId,
    regionId: job.regionId,
    running: job.running,
    stopping: job.stopping,
    usingProModel: job.usingProModel,
    mode: job.mode,
    progress: job.progress ? { ...job.progress } : null,
    items: job.items.map((item) => ({ ...item })),
    previewDrafts: job.previewDrafts.map((draft) => ({ ...draft })),
    error: job.error,
    offerProRetry: job.offerProRetry,
    modelFeedback: job.modelFeedback,
    hatchPrompt: job.hatchPrompt,
  };
}

function emptyJobShell(input: {
  key: string;
  assetId: string;
  regionId: string;
  items?: PasPracticeLibraryItem[];
  hatchPrompt?: string;
  modelFeedback?: string | null;
}): JobState {
  return {
    key: input.key,
    assetId: input.assetId,
    regionId: input.regionId,
    abort: false,
    running: false,
    stopping: false,
    usingProModel: false,
    mode: 'sequential',
    progress: null,
    items: refreshPasPracticeLibrary(input.items || []),
    previewDrafts: [],
    error: null,
    offerProRetry: false,
    modelFeedback: input.modelFeedback ?? null,
    hatchPrompt: input.hatchPrompt?.trim() || '',
    regionContent: '',
    regionFeedback: '',
    runToken: 0,
  };
}

class PasHatchSessionService {
  private jobs = new Map<string, JobState>();

  private listeners = new Map<string, Set<Listener>>();

  private assetListeners = new Map<string, Set<Listener>>();

  private assetPersists = new Map<string, PasHatchAssetPersist>();

  buildKey = buildKey;

  getSnapshot(assetId: string, regionId: string): PasHatchJobSnapshot | null {
    const job = this.jobs.get(buildKey(assetId, regionId));
    return job ? toSnapshot(job) : null;
  }

  isRunning(assetId: string, regionId: string): boolean {
    return this.jobs.get(buildKey(assetId, regionId))?.running === true;
  }

  listJobsForAsset(assetId: string): PasHatchJobSnapshot[] {
    const id = String(assetId || '').trim();
    if (!id) return [];
    const out: PasHatchJobSnapshot[] = [];
    for (const job of this.jobs.values()) {
      if (job.assetId === id) {
        out.push(toSnapshot(job));
      }
    }
    return out;
  }

  attachAssetPersist(assetId: string, persist: PasHatchAssetPersist) {
    const id = String(assetId || '').trim();
    if (!id) return;
    this.assetPersists.set(id, persist);
  }

  detachAssetPersist(assetId: string, nextPersist?: PasHatchAssetPersist | null) {
    const id = String(assetId || '').trim();
    if (!id) return;
    if (nextPersist) {
      this.assetPersists.set(id, nextPersist);
      return;
    }
    this.assetPersists.delete(id);
  }

  subscribe(assetId: string, regionId: string, listener: Listener): () => void {
    const key = buildKey(assetId, regionId);
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(listener);
    const job = this.jobs.get(key);
    if (job) {
      listener(toSnapshot(job));
    }
    return () => {
      set?.delete(listener);
      if (set && set.size === 0) {
        this.listeners.delete(key);
      }
    };
  }

  subscribeAsset(assetId: string, listener: Listener): () => void {
    const id = String(assetId || '').trim();
    if (!id) return () => {};
    let set = this.assetListeners.get(id);
    if (!set) {
      set = new Set();
      this.assetListeners.set(id, set);
    }
    set.add(listener);
    for (const job of this.jobs.values()) {
      if (job.assetId === id) {
        listener(toSnapshot(job));
      }
    }
    return () => {
      set?.delete(listener);
      if (set && set.size === 0) {
        this.assetListeners.delete(id);
      }
    };
  }

  /** Sync library edits (delete / ban) into the session cache and persist. */
  syncItems(
    assetId: string,
    regionId: string,
    items: PasPracticeLibraryItem[],
    options?: { hatchPrompt?: string; modelFeedback?: string | null },
  ) {
    const key = buildKey(assetId, regionId);
    const refreshed = refreshPasPracticeLibrary(items);
    let job = this.jobs.get(key);
    if (!job) {
      job = emptyJobShell({
        key,
        assetId: String(assetId || '').trim(),
        regionId: String(regionId || '').trim(),
        items: refreshed,
        hatchPrompt: options?.hatchPrompt,
        modelFeedback: options?.modelFeedback ?? null,
      });
      this.jobs.set(key, job);
    } else {
      job.items = refreshed;
      if (typeof options?.hatchPrompt === 'string') {
        job.hatchPrompt = options.hatchPrompt.trim();
      }
      if (options && Object.prototype.hasOwnProperty.call(options, 'modelFeedback')) {
        job.modelFeedback = options.modelFeedback ?? null;
      }
    }
    this.emit(job);
    void this.persist(job);
  }

  clearPreviewDrafts(assetId: string, regionId: string) {
    const job = this.jobs.get(buildKey(assetId, regionId));
    if (!job || job.previewDrafts.length === 0) return;
    job.previewDrafts = [];
    this.emit(job);
  }

  /** Merge preview pack into practice library and persist. */
  confirmPreviewDrafts(assetId: string, regionId: string): number {
    const job = this.jobs.get(buildKey(assetId, regionId));
    if (!job || job.previewDrafts.length === 0) return 0;
    const n = job.previewDrafts.length;
    job.items = refreshPasPracticeLibrary([
      ...job.items,
      ...job.previewDrafts.map((draft) => createPasPracticeLibraryItem({
        id: draft.id,
        question: draft.question,
        answer: draft.answer,
        notes: draft.notes,
      })),
    ]);
    job.previewDrafts = [];
    this.emit(job);
    void this.persist(job);
    return n;
  }

  stop(assetId: string, regionId: string) {
    const job = this.jobs.get(buildKey(assetId, regionId));
    if (!job || !job.running || job.abort) return;
    job.abort = true;
    job.stopping = true;
    this.emit(job);
  }

  start(input: PasHatchStartInput): PasHatchJobSnapshot {
    const assetId = String(input.assetId || '').trim();
    const regionId = String(input.regionId || '').trim();
    const key = buildKey(assetId, regionId);
    const existing = this.jobs.get(key);
    if (existing?.running) {
      return toSnapshot(existing);
    }

    const trimmed = String(input.hatchPrompt || '').trim();
    const useProModel = input.useProModel === true;
    const mode: PasHatchMode = input.mode === 'english_batch' ? 'english_batch' : 'sequential';
    const total = mode === 'english_batch'
      ? 1
      : Math.max(1, Math.min(9, Number(input.count) || 1));
    const initialItems = refreshPasPracticeLibrary(input.initialItems || []);

    const job: JobState = {
      key,
      assetId,
      regionId,
      abort: false,
      running: true,
      stopping: false,
      usingProModel: useProModel,
      mode,
      progress: { current: 0, total, produced: 0, failed: 0 },
      items: initialItems,
      // Keep prior preview only if not starting a new batch; new start clears it.
      previewDrafts: [],
      error: null,
      offerProRetry: false,
      modelFeedback: input.regionFeedback?.trim() || existing?.modelFeedback || null,
      hatchPrompt: trimmed,
      regionContent: String(input.regionContent || '').trim(),
      regionFeedback: String(input.regionFeedback || '').trim(),
      cropRectNormalized: input.cropRectNormalized,
      sourceKpId: input.sourceKpId,
      subject: input.subject || null,
      runToken: (existing?.runToken || 0) + 1,
    };
    this.jobs.set(key, job);
    this.emit(job);

    const complexity = assessPasHatchComplexity(trimmed);
    if (complexity.warning && !useProModel) {
      this.toast(
        job,
        complexity.level === 'high'
          ? '孵化词偏复杂，仍继续后台生成；若失败可再试 Pro 模型'
          : '孵化词略复杂，仍继续后台生成',
        input.onToast,
      );
    }

    if (mode === 'english_batch') {
      this.toast(job, '已交给后台生成变式包，可关闭抽屉，完成后回来预览确认', input.onToast);
      void this.runEnglishBatch(job, useProModel, input.onToast);
    } else {
      void this.runLoop(job, total, useProModel, input.onToast);
    }
    return toSnapshot(job);
  }

  private toast(
    job: JobState,
    message: string,
    startToast?: (message: string) => void,
  ) {
    const trimmed = String(message || '').trim();
    if (!trimmed) return;
    // Never toast long vision OCR complaints during hatch — they look like generate failures.
    if (isVisionStyleComplaint(trimmed)) return;
    startToast?.(trimmed);
    this.assetPersists.get(job.assetId)?.onToast?.(trimmed);
  }

  private emit(job: JobState) {
    const snapshot = toSnapshot(job);
    const keyListeners = this.listeners.get(job.key);
    if (keyListeners) {
      for (const listener of keyListeners) {
        listener(snapshot);
      }
    }
    const assetSet = this.assetListeners.get(job.assetId);
    if (assetSet) {
      for (const listener of assetSet) {
        listener(snapshot);
      }
    }
  }

  private async persist(job: JobState) {
    const handler = this.assetPersists.get(job.assetId);
    if (!handler) return;
    try {
      await handler.persistItems(job.regionId, job.items);
    } catch {
      // Persist failures surface via workbench toast when attached.
    }
  }

  private async runEnglishBatch(
    job: JobState,
    useProModel: boolean,
    startToast?: (message: string) => void,
  ) {
    const token = job.runToken;
    try {
      if (!job.regionContent.trim()) {
        this.toast(job, '无识图文本：将主要依据孵化词+关联 KP 出题', startToast);
      }
      if (this.jobs.get(job.key) !== job || job.runToken !== token || job.abort) {
        return;
      }

      job.progress = { current: 1, total: 1, produced: 0, failed: 0 };
      this.emit(job);

      const negativeExamples = listPasPracticeNegativeExampleQuestions(job.items);
      const avoidQuestions = [
        ...job.items.map((item) => String(item.question || '').trim()).filter(Boolean),
        ...negativeExamples,
      ];

      const result = await generatePasPracticeDraftBatch({
        assetId: job.assetId,
        regionId: job.regionId,
        sourceKpId: job.sourceKpId,
        hatchPrompt: job.hatchPrompt,
        subject: job.subject,
        regionContent: job.regionContent || undefined,
        existingCount: job.items.length,
        avoidQuestions,
        negativeExamples,
        autoCount: true,
        useProModel,
      });

      if (this.jobs.get(job.key) !== job || job.runToken !== token) {
        return;
      }
      if (job.abort) {
        job.error = '已停止生成';
        return;
      }

      if (!result.success || !result.drafts.length) {
        job.error = asHatchFailMessage(result.message || result.error || '批量生成失败');
        job.offerProRetry = !useProModel;
        job.progress = { current: 1, total: 1, produced: 0, failed: 1 };
        this.emit(job);
        return;
      }

      job.previewDrafts = result.drafts;
      job.error = null;
      job.offerProRetry = false;
      job.progress = { current: 1, total: 1, produced: result.drafts.length, failed: 0 };
      const planHint = result.countPlan?.wordListMode
        ? `（词表约 ${result.countPlan.wordPoolSize || result.drafts.length} 词）`
        : '';
      this.toast(
        job,
        `变式包已就绪：${result.drafts.length} 道${planHint}，请预览确认后入库`,
        startToast,
      );
      this.emit(job);
    } catch (err) {
      if (this.jobs.get(job.key) !== job || job.runToken !== token) {
        return;
      }
      if (job.abort) {
        job.error = '已停止生成';
      } else {
        job.error = err instanceof Error ? err.message : '批量生成失败';
        job.offerProRetry = !useProModel;
      }
      this.emit(job);
    } finally {
      if (this.jobs.get(job.key) === job && job.runToken === token) {
        job.running = false;
        job.stopping = false;
        job.progress = null;
        job.usingProModel = false;
        job.abort = false;
        this.emit(job);
      }
    }
  }

  private async runLoop(
    job: JobState,
    total: number,
    useProModel: boolean,
    startToast?: (message: string) => void,
  ) {
    const token = job.runToken;
    // Flash: one attempt per slot (fast). Pro: one retry.
    const slotAttempts = useProModel ? 2 : 1;
    let produced = 0;
    let failedSlots = 0;
    let lastFailMessage = '';

    try {
      // Text-only DeepSeek hatch — never call qwen / region-vision here.
      if (!job.regionContent.trim()) {
        this.toast(job, '无识图文本：将主要依据孵化词+关联 KP 出题', startToast);
      }
      if (this.jobs.get(job.key) !== job || job.runToken !== token || job.abort) {
        return;
      }

      for (let i = 0; i < total; i += 1) {
        if (this.jobs.get(job.key) !== job || job.runToken !== token) {
          return;
        }
        if (job.abort) {
          job.error = produced > 0 ? `已停止：本批完成 ${produced}/${total} 题` : '已停止生成';
          break;
        }

        job.progress = { current: i + 1, total, produced, failed: failedSlots };
        this.emit(job);

        let draft: Awaited<ReturnType<typeof generatePasPracticeDraftOne>>['draft'] = null;
        for (let attempt = 0; attempt < slotAttempts; attempt += 1) {
          if (job.abort) break;
          const existing = job.items.length;
          const negativeExamples = listPasPracticeNegativeExampleQuestions(job.items);
          const avoidQuestions = [
            ...job.items.map((item) => String(item.question || '').trim()).filter(Boolean),
            ...negativeExamples,
          ];
          // Do NOT send cropRectNormalized / regionFeedback — old proxies may re-trigger vision.
          const result = await generatePasPracticeDraftOne({
            assetId: job.assetId,
            regionId: job.regionId,
            sourceKpId: job.sourceKpId,
            hatchPrompt: job.hatchPrompt,
            subject: job.subject,
            regionContent: job.regionContent || undefined,
            variantIndex: existing + 1,
            existingCount: existing,
            avoidQuestions,
            negativeExamples,
            useProModel,
          });
          if (this.jobs.get(job.key) !== job || job.runToken !== token) {
            return;
          }
          if (result.success && result.draft) {
            draft = result.draft;
            break;
          }
          lastFailMessage = asHatchFailMessage(
            result.message || result.error || result.feedback || '生成失败',
          );
          if (attempt < slotAttempts - 1 && !job.abort) {
            await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
          }
        }

        if (job.abort) {
          job.error = produced > 0 ? `已停止：本批完成 ${produced}/${total} 题` : '已停止生成';
          break;
        }
        if (!draft) {
          failedSlots += 1;
          job.progress = { current: i + 1, total, produced, failed: failedSlots };
          this.emit(job);
          continue;
        }

        produced += 1;
        job.items = refreshPasPracticeLibrary([
          ...job.items,
          createPasPracticeLibraryItem({
            id: draft.id,
            question: draft.question,
            answer: draft.answer,
            notes: draft.notes,
          }),
        ]);
        job.progress = { current: i + 1, total, produced, failed: failedSlots };
        this.emit(job);
        await this.persist(job);
      }

      if (this.jobs.get(job.key) !== job || job.runToken !== token) {
        return;
      }

      if (job.abort) {
        // stop message already set
      } else if (produced >= total) {
        job.error = null;
        job.offerProRetry = false;
        this.toast(
          job,
          useProModel ? `已用 Pro 模型生成 ${produced} 道变式` : `已生成 ${produced} 道变式`,
          startToast,
        );
      } else if (produced > 0) {
        job.error = `本批成功 ${produced}/${total} 题；其余未生成${lastFailMessage ? `（${lastFailMessage}）` : ''}。可再点「补足」继续。`;
        job.offerProRetry = !useProModel;
      } else {
        job.error = lastFailMessage || '生成失败，请重试';
        job.offerProRetry = !useProModel;
      }
    } catch (err) {
      if (this.jobs.get(job.key) !== job || job.runToken !== token) {
        return;
      }
      job.error = produced > 0
        ? `本批已成功 ${produced} 题后中断：${err instanceof Error ? err.message : '生成失败'}`
        : (err instanceof Error ? err.message : '生成失败');
      job.offerProRetry = produced === 0 && !useProModel;
    } finally {
      if (this.jobs.get(job.key) === job && job.runToken === token) {
        job.running = false;
        job.stopping = false;
        job.progress = null;
        job.usingProModel = false;
        job.abort = false;
        this.emit(job);
      }
    }
  }
}

export const pasHatchSessionService = new PasHatchSessionService();
